import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import createPDF from '@app/utils/createPDF';
import { Notification } from '@app/components/notifications/Notifications';

type SendNotification = (
    message: string,
    config?: Notification['options'],
) => void;

type DownloadBroadcastReportParams = {
    reqBody: Record<string, unknown>;
    filename: string;
    sendNotification: SendNotification;
};

/**
 * Downloads the per-exposure broadcast PDF through the Sports Report Service.
 *
 * Mirrors `useGeneratePDF`: the request is wrapped so a failure (e.g. SRS
 * answering 500 because its sports-api auth check returned 401) is reported
 * to Sentry and surfaced to the user instead of failing silently, and the
 * loading state is always cleared.
 */
const useDownloadBroadcastReport = () => {
    const { t } = useTranslation();
    const [isDownloadingBroadcastReport, setIsDownloadingBroadcastReport] =
        useState(false);

    const downloadBroadcastReport = useCallback(
        async ({
            reqBody,
            filename,
            sendNotification,
        }: DownloadBroadcastReportParams) => {
            setIsDownloadingBroadcastReport(true);
            try {
                await createPDF({
                    reqBody,
                    type: 'broadcast-pdf',
                    document,
                    filename,
                    notif: () =>
                        sendNotification(
                            t(
                                'measurement:broadcastExposure.detailsModal.downloading',
                            ),
                            { context: 'success' },
                        ),
                });
            } catch (error) {
                Sentry.withScope(scope => {
                    scope.setTag('context', 'broadcast-pdf-download');
                    scope.setLevel('error');
                    scope.setExtra(
                        'srsStatus',
                        (error as { status?: number })?.status,
                    );
                    Sentry.captureException(error);
                });
                sendNotification(
                    t(
                        'measurement:broadcastExposure.detailsModal.downloadFailed',
                    ),
                    { context: 'error' },
                );
            } finally {
                setIsDownloadingBroadcastReport(false);
            }
        },
        [t],
    );

    return { isDownloadingBroadcastReport, downloadBroadcastReport };
};

export default useDownloadBroadcastReport;
